// src/components/Experiment/InferenceResultCard.js
// Result of one prediction run on unknown samples (AnalysisInference)
import React, { useState, useMemo } from 'react';
import { Card, Button, Modal, Badge, Spinner, Alert, Table, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

// Rows shown before "Show all"
const PREVIEW_ROWS = 50;

// Same format as AnalysisResultCard.formatMetricValue (fractions, 4 digits)
const formatMetricValue = (value) => {
  if (typeof value === 'number') return value.toFixed(4);
  return value === null || value === undefined ? 'N/A' : value;
};

const getTopProbability = (probabilities) => {
  if (!probabilities) return null;
  const values = Object.values(probabilities);
  return values.length > 0 ? Math.max(...values) : null;
};

// Source rows of an averaged sample: "1-3" for a contiguous block, otherwise "1, 4, 7"
const formatRows = (prediction) => {
  const rows = prediction.rows || [prediction.row];
  if (rows.length > 1 && rows.every((row, idx) => idx === 0 || row === rows[idx - 1] + 1)) {
    return `${rows[0]}-${rows[rows.length - 1]}`;
  }
  return rows.join(', ');
};

const csvEscape = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const InferenceResultCard = ({ inference, modelUpdatedAt, onDelete }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);

  const results = inference.results || {};
  const predictions = useMemo(() => results.predictions || [], [results.predictions]);
  const metrics = results.metrics;
  const probabilityClasses = results.probability_classes || [];
  const hasTrueClasses = predictions.some(p => p.true_class || p.label_conflict);
  // Replicates averaged by sample_name: one prediction per sample (rows: source rows)
  const isAveraged = !!results.averaged;
  const showLabelColumn = inference.label_column && inference.label_column !== 'sample_name';

  // The analysis was rerun after this prediction (1s tolerance for timestamp rounding)
  const isModelOutdated = !!(modelUpdatedAt && inference.model_trained_at &&
    new Date(modelUpdatedAt).getTime() - new Date(inference.model_trained_at).getTime() > 1000);

  const getStatusVariant = () => {
    switch (inference.status) {
      case 'completed': return 'success';
      case 'running': return 'primary';
      case 'failed': return 'danger';
      default: return 'secondary';
    }
  };

  const handleDownloadCsv = () => {
    const header = [isAveraged ? 'rows' : 'row', ...(isAveraged ? ['n_replicates'] : []), 'sample',
      ...(showLabelColumn ? [inference.label_column, 'true_class'] : []),
      'predicted_class', ...probabilityClasses.map(cls => `p(${cls})`)];
    const lines = [header.map(csvEscape).join(',')];
    predictions.forEach(p => {
      const line = [
        isAveraged ? (p.rows || [p.row]).join(';') : p.row,
        ...(isAveraged ? [p.n_replicates ?? 1] : []),
        p.sample,
        ...(showLabelColumn ? [p.label, p.true_class || ''] : []),
        p.predicted_class,
        ...probabilityClasses.map(cls => (p.probabilities ? p.probabilities[cls] : ''))
      ];
      lines.push(line.map(csvEscape).join(','));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(inference.name || 'prediction').replace(/[^\w-]+/g, '_')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Metric cards follow AnalysisResultCard ("Model self-test on estimation samples",
  // "Class Metrics (CV Average)", "Cross-Validation Confusion Matrix"): values as
  // fractions (formatMetricValue), a Badge with the same color per metric
  const renderPredictionMetricsCard = () => {
    const isBinary = (results.classes || []).length === 2;
    return (
      <Card className="h-100">
        <Card.Header className="py-2">
          <h6 className="mb-0">Prediction Metrics</h6>
        </Card.Header>
        <Card.Body className="py-2">
          <Table size="sm" className="mb-0">
            <tbody>
              <tr>
                <td><strong>Accuracy:</strong></td>
                <td className="text-end">
                  <Badge bg="primary">{formatMetricValue(metrics.accuracy)}</Badge>
                </td>
              </tr>
              <tr>
                <td><strong>Precision:</strong></td>
                <td className="text-end">
                  <Badge bg="info">{formatMetricValue(metrics.precision)}</Badge>
                </td>
              </tr>
              {isBinary ? (
                <>
                  <tr>
                    <td><strong>Sensitivity:</strong></td>
                    <td className="text-end">
                      <Badge bg="danger">{formatMetricValue(metrics.sensitivity)}</Badge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Specificity:</strong></td>
                    <td className="text-end">
                      <Badge bg="warning">{formatMetricValue(metrics.specificity)}</Badge>
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td><strong>Recall:</strong></td>
                  <td className="text-end">
                    <Badge bg="danger">{formatMetricValue(metrics.recall)}</Badge>
                  </td>
                </tr>
              )}
              <tr>
                <td><strong>F1 Score:</strong></td>
                <td className="text-end">
                  <Badge bg="success">{formatMetricValue(metrics.f1)}</Badge>
                </td>
              </tr>
              <tr>
                <td><strong>Mapped Samples:</strong></td>
                <td className="text-end">
                  <Badge bg="secondary">{metrics.n_mapped} / {metrics.n_total}</Badge>
                </td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const renderClassMetricsCard = () => {
    const report = metrics.classification_report || {};
    const classNames = (metrics.confusion_matrix_labels || results.classes || [])
      .filter(className => report[className]);

    return (
      <Card className="h-100">
        <Card.Header className="py-2">
          <h6 className="mb-0">Class Metrics</h6>
        </Card.Header>
        <Card.Body className="py-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {classNames.length > 0 ? (
            <div className="class-metrics-table">
              {classNames.map(className => (
                <div key={className} className="class-metric-item mb-2 p-2 border rounded">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="small fw-bold text-truncate" title={className}>
                      {className}
                    </span>
                    <Badge bg="secondary" pill title="Support (mapped samples of this class)">
                      {report[className].support}
                    </Badge>
                  </div>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="small">Precision:</td>
                        <td className="text-end">
                          <Badge bg="success" className="small">
                            {formatMetricValue(report[className].precision)}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="small">Recall:</td>
                        <td className="text-end">
                          <Badge bg="info" className="small">
                            {formatMetricValue(report[className].recall)}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="small">F1-Score:</td>
                        <td className="text-end">
                          <Badge bg="warning" className="small">
                            {formatMetricValue(report[className]['f1-score'])}
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted">
              <small>No class metrics available</small>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderPredictedClassesCard = () => {
    const predictedCounts = results.predicted_counts || {};
    return (
      <Card className="h-100">
        <Card.Header className="py-2">
          <h6 className="mb-0">Predicted Classes</h6>
        </Card.Header>
        <Card.Body className="py-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <Table size="sm" className="mb-0">
            <tbody>
              {Object.entries(predictedCounts).map(([cls, count]) => (
                <tr key={cls}>
                  <td><strong>{cls}:</strong></td>
                  <td className="text-end">
                    <Badge bg="info">{count}</Badge>
                  </td>
                </tr>
              ))}
              <tr>
                <td><strong>Total Samples:</strong></td>
                <td className="text-end">
                  <Badge bg="secondary">{results.n_samples ?? predictions.length}</Badge>
                </td>
              </tr>
              {isAveraged && (
                <tr>
                  <td><strong>Source Rows:</strong></td>
                  <td className="text-end">
                    <Badge bg="secondary">{results.n_rows}</Badge>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const renderConfusionMatrix = () => {
    const labels = metrics.confusion_matrix_labels || results.classes || [];
    return (
      <Row className="mt-2">
        <Col>
          <Card>
            <Card.Header className="py-2">
              <h6 className="mb-0">Confusion Matrix (Mapped Samples)</h6>
            </Card.Header>
            <Card.Body className="py-2">
              <div className="confusion-matrix-compact">
                <Table size="sm" bordered className="text-center mb-0">
                  <thead>
                    <tr>
                      <th width="20%"></th>
                      {labels.map(label => (
                        <th key={label} className="bg-light small">P:{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(metrics.confusion_matrix || []).map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <th className="bg-light small">A:{labels[rowIdx]}</th>
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            className={rowIdx === cellIdx && cell > 0 ? 'table-success' : ''}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderMetrics = () => (
    <div className="classification-results mb-2">
      <Row className="g-2">
        {metrics ? (
          <>
            <Col md={4}>{renderPredictionMetricsCard()}</Col>
            <Col md={4}>{renderClassMetricsCard()}</Col>
            <Col md={4}>{renderPredictedClassesCard()}</Col>
          </>
        ) : (
          <>
            <Col md={4}>{renderPredictedClassesCard()}</Col>
            <Col md={8}>
              <Alert variant="light" className="border py-2 small mb-0">
                <i className="bi bi-info-circle me-1"></i>
                No true classes were mapped — only predictions are shown. Choose a class column
                and map its values to model classes to get accuracy, precision, recall, F1 and a
                confusion matrix.
              </Alert>
            </Col>
          </>
        )}
      </Row>
      {metrics && renderConfusionMatrix()}
    </div>
  );

  const renderPredictionsTable = () => {
    const rows = showAllRows ? predictions : predictions.slice(0, PREVIEW_ROWS);

    return (
      <>
        <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <Table size="sm" hover className="mb-0 small">
            <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
              <tr>
                <th>{isAveraged ? 'Rows' : 'Row'}</th>
                {isAveraged && <th>Replicates</th>}
                <th>Sample</th>
                {showLabelColumn && <th>{inference.label_column}</th>}
                {hasTrueClasses && <th>True class</th>}
                <th>Predicted class</th>
                {probabilityClasses.length > 0 && <th>Confidence</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(p => {
                const topProbability = getTopProbability(p.probabilities);
                let rowClass = '';
                if (p.true_class) {
                  rowClass = p.true_class === p.predicted_class ? 'table-success' : 'table-danger';
                }
                return (
                  <tr key={p.row} className={rowClass}>
                    <td className="text-muted">{isAveraged ? formatRows(p) : p.row}</td>
                    {isAveraged && <td><Badge bg="secondary" pill>{p.n_replicates ?? 1}</Badge></td>}
                    <td>{p.sample}</td>
                    {showLabelColumn && <td>{p.label}</td>}
                    {hasTrueClasses && (
                      <td>
                        {p.label_conflict ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Replicates of this sample are mapped to different classes - excluded from metrics</Tooltip>}
                          >
                            <Badge bg="warning" text="dark">conflict</Badge>
                          </OverlayTrigger>
                        ) : (p.true_class || <span className="text-muted">—</span>)}
                      </td>
                    )}
                    <td><Badge bg="info">{p.predicted_class}</Badge></td>
                    {probabilityClasses.length > 0 && (
                      <td>
                        {topProbability !== null ? (
                          <OverlayTrigger
                            placement="left"
                            overlay={
                              <Tooltip>
                                {Object.entries(p.probabilities).map(([cls, prob]) => (
                                  <div key={cls}>{cls}: {formatMetricValue(prob)}</div>
                                ))}
                              </Tooltip>
                            }
                          >
                            <span>{formatMetricValue(topProbability)}</span>
                          </OverlayTrigger>
                        ) : '—'}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        {predictions.length > PREVIEW_ROWS && (
          <Button variant="link" size="sm" className="p-0 mt-1" onClick={() => setShowAllRows(!showAllRows)}>
            {showAllRows ? `Show first ${PREVIEW_ROWS} rows` : `Show all ${predictions.length} rows`}
          </Button>
        )}
      </>
    );
  };

  const renderBody = () => {
    if (inference.status === 'pending' || inference.status === 'running' || inference.status === 'queued') {
      return (
        <div className="text-muted small py-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Predicting...
        </div>
      );
    }

    if (inference.status === 'failed') {
      return (
        <Alert variant="danger" className="py-2 mb-0 small">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {inference.error_message || 'Prediction failed'}
        </Alert>
      );
    }

    if (inference.status !== 'completed') return null;

    return (
      <>
        {renderMetrics()}
        {renderPredictionsTable()}
      </>
    );
  };

  return (
    <>
      <Card className="inference-result-card">
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="flex-grow-1">
              <h6 className="mb-1 fw-bold">{inference.name || 'Prediction'}</h6>
              <div className="d-flex align-items-center gap-2 flex-wrap small">
                <Badge bg={getStatusVariant()}>{inference.status}</Badge>
                <span className="text-muted">
                  <i className="bi bi-folder2-open me-1"></i>
                  {inference.source_experiment_name || 'Deleted experiment'}
                  {!inference.source_experiment && inference.source_experiment_name && ' (deleted)'}
                </span>
                <code className="small">{inference.data_range}</code>
                {inference.status === 'completed' && (
                  <Badge bg="secondary">{results.n_samples ?? predictions.length} samples</Badge>
                )}
                {inference.status === 'completed' && isAveraged && (
                  <Badge bg="warning" text="dark">Averaged replicates</Badge>
                )}
                {isModelOutdated && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>The analysis was rerun after this prediction. Results were produced by the previous model.</Tooltip>}
                  >
                    <Badge bg="warning" text="dark">Model retrained since</Badge>
                  </OverlayTrigger>
                )}
                <small className="text-muted">{new Date(inference.created_at).toLocaleString()}</small>
              </div>
            </div>
            <div className="d-flex gap-1 flex-shrink-0">
              {inference.status === 'completed' && predictions.length > 0 && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDownloadCsv}
                  title="Download predictions (CSV)"
                >
                  <i className="bi bi-download"></i>
                </Button>
              )}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setShowDeleteModal(true)}
                title="Delete prediction"
              >
                <i className="bi bi-trash"></i>
              </Button>
            </div>
          </div>

          {renderBody()}
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} size="sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6">Delete Prediction</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Alert variant="warning" className="py-2 mb-2">
            <small>Delete "{inference.name || 'Prediction'}"? This cannot be undone.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={() => { onDelete(); setShowDeleteModal(false); }}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InferenceResultCard;
